import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Package, Plus, Pencil, Trash2, Loader2, X, AlertCircle, Upload, Search } from "lucide-react";
import {
  getAdminProducts, createAdminProduct, updateAdminProduct, deleteAdminProduct,
  uploadProductImage, AdminProduct, ProductCategory, StockStatus,
} from "../../api/services/adminStoreService";
import { getErrorMessage } from "@/utils/errorUtils";

const CATEGORIES: ProductCategory[] = [
  "hotspot", "indoor_kits", "outdoor_kits", "accessories", "network_solutions", "support",
];
const STOCK: StockStatus[] = ["in_stock", "low_stock", "out_of_stock"];
const CAT_LABEL: Record<string, string> = {
  hotspot: "Hotspot", indoor_kits: "Indoor Kit", outdoor_kits: "Outdoor Kit",
  accessories: "Accessory", network_solutions: "Network Solution", support: "Support",
};

const emptyProduct = (): Partial<AdminProduct> => ({
  name: "", category: "hotspot", price: 0, currency: "USD", availability: true,
  stockStatus: "in_stock", shortDescription: "", fullDescription: "", thumbnailUrl: "", images: [],
});

const fld = "w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500";

export default function AdminProducts() {
  const { t } = useTranslation();
  const [products, setProducts] = useState<AdminProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState<Partial<AdminProduct> | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<AdminProduct | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    setLoading(true); setErr("");
    try { setProducts(await getAdminProducts()); }
    catch (e: unknown) { setErr(getErrorMessage(e) || "Failed to load products"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(emptyProduct()); setModalOpen(true); setErr(""); };
  const openEdit = (p: AdminProduct) => { setEditing({ ...p }); setModalOpen(true); setErr(""); };
  const closeModal = () => { setModalOpen(false); setEditing(null); };

  const onUpload = async (file: File | undefined) => {
    if (!file || !editing) return;
    setUploading(true); setErr("");
    try {
      const url = await uploadProductImage(file);
      if (url) setEditing({ ...editing, thumbnailUrl: url });
    } catch (e: unknown) { setErr(getErrorMessage(e) || "Image upload failed"); }
    finally { setUploading(false); }
  };

  const save = async () => {
    if (!editing) return;
    if (!editing.name || editing.price === undefined || editing.price === null || (editing.price as string | number) === "") {
      setErr("Name and price are required"); return;
    }
    setSaving(true); setErr("");
    const norm = (v?: string) => (v || "").toLowerCase().replace(/\s+/g, "_");
    // Whitelist only editable schema fields (avoid sending _id, timestamps, rating,
    // reviewCount which trip runValidators), and normalize enum values so legacy
    // mixed-case data (e.g. "Hotspot") is coerced to the schema enum ("hotspot").
    const payload: Partial<AdminProduct> = {
      name: editing.name,
      category: norm(editing.category) as ProductCategory,
      price: Number(editing.price),
      currency: editing.currency || "USD",
      availability: !!editing.availability,
      stockStatus: norm(editing.stockStatus) as StockStatus,
      shortDescription: editing.shortDescription || "",
      fullDescription: editing.fullDescription || "",
      thumbnailUrl: editing.thumbnailUrl || "",
      images: editing.thumbnailUrl ? [editing.thumbnailUrl, ...(editing.images || []).filter(i => i !== editing.thumbnailUrl)] : (editing.images || []),
    };
    try {
      if (editing._id) await updateAdminProduct(editing._id, payload);
      else await createAdminProduct(payload);
      closeModal(); await load();
    } catch (e: unknown) {
      setErr(getErrorMessage(e) || "Failed to save product");
    } finally { setSaving(false); }
  };

  const doDelete = async (p: AdminProduct) => {
    if (!p._id) return;
    setBusyId(p._id); setErr("");
    try { await deleteAdminProduct(p._id); setConfirmDelete(null); await load(); }
    catch (e: unknown) { setErr(getErrorMessage(e) || "Failed to delete product"); }
    finally { setBusyId(null); }
  };

  const filtered = products.filter(p =>
    !search || p.name?.toLowerCase().includes(search.toLowerCase()) || CAT_LABEL[p.category || ""]?.toLowerCase().includes(search.toLowerCase())
  );

  const stockBadge = (s?: string) => {
    const map: Record<string, string> = {
      in_stock: "bg-green-100 text-green-700 dark:bg-green-950/40 dark:text-green-400",
      low_stock: "bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400",
      out_of_stock: "bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-400",
    };
    return map[s || "in_stock"] || map.in_stock;
  };

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-2">
          <Package className="w-6 h-6 text-blue-600" />
          <h1 className="text-xl sm:text-2xl font-bold">{t('admin.products.title', 'Products')}</h1>
          <span className="text-sm text-gray-500">({products.length})</span>
        </div>
        <button onClick={openNew} className="inline-flex items-center gap-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 text-sm font-medium">
          <Plus className="w-4 h-4" /> {t('admin.products.button.newproduct', 'New Product')}
        </button>
      </div>

      <div className="relative mb-4 max-w-sm">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input className={fld + " pl-9"} placeholder={t('admin.products.search', 'Search products…')} value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>

      {err && !modalOpen && (
        <div className="mb-4 flex items-center gap-2 rounded-lg bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-400 px-4 py-3 text-sm">
          <AlertCircle className="w-4 h-4" /> {err}
        </div>
      )}

      <Card className="overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-gray-500">{t('admin.products.noProducts', 'No products found.')}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800/50 text-left text-gray-500">
                <tr>
                  <th className="px-4 py-3 font-medium">{t('admin.products.table.product', 'Product')}</th>
                  <th className="px-4 py-3 font-medium">{t('admin.products.table.category', 'Category')}</th>
                  <th className="px-4 py-3 font-medium">{t('admin.products.table.price', 'Price')}</th>
                  <th className="px-4 py-3 font-medium">{t('admin.products.table.stock', 'Stock')}</th>
                  <th className="px-4 py-3 font-medium">{t('admin.products.table.active', 'Active')}</th>
                  <th className="px-4 py-3 font-medium text-right">{t('admin.products.table.actions', 'Actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {filtered.map((p) => (
                  <tr key={p._id} className="hover:bg-gray-50 dark:hover:bg-gray-800/40">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-gray-800 overflow-hidden flex-shrink-0">
                          {p.thumbnailUrl ? <img src={p.thumbnailUrl} alt={p.name} className="w-full h-full object-cover" /> : null}
                        </div>
                        <div>
                          <div className="font-medium">{p.name}</div>
                          {p.shortDescription ? <div className="text-xs text-gray-500 truncate max-w-[240px]">{p.shortDescription}</div> : null}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">{CAT_LABEL[p.category || ""] || p.category}</td>
                    <td className="px-4 py-3 font-medium">{p.currency || "USD"} {Number(p.price).toFixed(2)}</td>
                    <td className="px-4 py-3"><span className={"px-2 py-1 rounded-full text-xs font-medium " + stockBadge(p.stockStatus)}>{t(`admin.products.stock.${p.stockStatus || "in_stock"}`, (p.stockStatus || "in_stock").replace("_", " ").charAt(0).toUpperCase() + (p.stockStatus || "in_stock").replace("_", " ").slice(1))}</span></td>
                    <td className="px-4 py-3">{p.availability ? <span className="text-green-600">●</span> : <span className="text-gray-400">○</span>}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => openEdit(p)} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => setConfirmDelete(p)} className="p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-950/30 text-red-600"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {modalOpen && editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={closeModal}>
          <div className="bg-white dark:bg-gray-900 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-800 sticky top-0 bg-white dark:bg-gray-900">
              <h2 className="text-lg font-semibold">{editing._id ? t('admin.products.modal.edit', 'Edit Product') : t('admin.products.modal.new', 'New Product')}</h2>
              <button onClick={closeModal} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              {err && <div className="flex items-center gap-2 rounded-lg bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-400 px-3 py-2 text-sm"><AlertCircle className="w-4 h-4" /> {err}</div>}

              <div>
                <label className="block text-sm font-medium mb-1">{t('admin.products.form.name', 'Name')}</label>
                <input className={fld} value={editing.name || ""} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="e.g. Smart WiFi Hotspot Kit" />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">{t('admin.products.form.category', 'Category')}</label>
                  <select className={fld} value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value as ProductCategory })}>
                    {CATEGORIES.map(c => <option key={c} value={c}>{CAT_LABEL[c]}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">{t('admin.products.form.stock', 'Stock')}</label>
                  <select className={fld} value={editing.stockStatus} onChange={(e) => setEditing({ ...editing, stockStatus: e.target.value as StockStatus })}>
                    {STOCK.map(s => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">{t('admin.products.form.price', 'Price')}</label>
                  <input type="number" className={fld} value={editing.price ?? 0} onChange={(e) => setEditing({ ...editing, price: e.target.value as unknown as number })} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">{t('admin.products.form.currency', 'Currency')}</label>
                  <input className={fld} value={editing.currency || "USD"} onChange={(e) => setEditing({ ...editing, currency: e.target.value })} />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">{t('admin.products.form.shortDescription', 'Short description')}</label>
                <input className={fld} value={editing.shortDescription || ""} onChange={(e) => setEditing({ ...editing, shortDescription: e.target.value })} placeholder="Shown on store cards" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{t('admin.products.form.fullDescription', 'Full description')}</label>
                <textarea className={fld} rows={3} value={editing.fullDescription || ""} onChange={(e) => setEditing({ ...editing, fullDescription: e.target.value })} placeholder="Shown on the product detail page" />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">{t('admin.products.form.productImage', 'Product image')}</label>
                <div className="flex items-center gap-3">
                  <div className="w-16 h-16 rounded-lg bg-gray-100 dark:bg-gray-800 overflow-hidden flex-shrink-0">
                    {editing.thumbnailUrl ? <img src={editing.thumbnailUrl} alt="" className="w-full h-full object-cover" /> : null}
                  </div>
                  <label className="inline-flex items-center gap-2 rounded-lg border border-gray-300 dark:border-gray-700 px-3 py-2 text-sm cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800">
                    {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} {t('admin.products.button.upload', 'Upload')}
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => onUpload(e.target.files?.[0])} />
                  </label>
                </div>
                <input className={fld + " mt-2"} value={editing.thumbnailUrl || ""} onChange={(e) => setEditing({ ...editing, thumbnailUrl: e.target.value })} placeholder={t('admin.products.form.pasteUrl', '…or paste an image URL')} />
              </div>

              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!editing.availability} onChange={(e) => setEditing({ ...editing, availability: e.target.checked })} />
                {t('admin.products.form.activeStore', 'Active (visible in store)')}
              </label>
            </div>
            <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-100 dark:border-gray-800">
              <button onClick={closeModal} className="rounded-lg border border-gray-300 dark:border-gray-700 px-4 py-2 text-sm">{t('admin.products.button.cancel', 'Cancel')}</button>
              <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 text-sm font-medium disabled:opacity-50">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null} {editing._id ? t('admin.products.button.saveChanges', 'Save changes') : t('admin.products.button.createProduct', 'Create product')}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setConfirmDelete(null)}>
          <div className="bg-white dark:bg-gray-900 rounded-2xl w-full max-w-sm shadow-xl p-5" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-2">{t('admin.products.modal.deleteTitle', 'Delete product?')}</h3>
            <p className="text-sm text-gray-500 mb-5">{t('admin.products.modal.deleteWarning', '"{name}" will be permanently removed.', { name: confirmDelete.name })}</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirmDelete(null)} className="rounded-lg border border-gray-300 dark:border-gray-700 px-4 py-2 text-sm">{t('admin.products.button.cancel', 'Cancel')}</button>
              <button onClick={() => doDelete(confirmDelete)} disabled={busyId === confirmDelete._id} className="inline-flex items-center gap-2 rounded-lg bg-red-600 hover:bg-red-700 text-white px-4 py-2 text-sm font-medium disabled:opacity-50">
                {busyId === confirmDelete._id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />} {t('admin.products.button.delete', 'Delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
